import { type FC, useEffect, useRef, useState } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import AdTag from "@/src/common/AdTag/AdTag";
import type { IAdTagViewPortSizes } from "@/src/common/AdTag/AdTag";
import { useViewportInfoContext } from "@/src/contexts";
import type { IFlyerCatalogueImage } from "@/src/flyers/FlyerCatalogue/FlyerCatalogueHelpers";
import { FlyerEventType, TelemetryEntity } from "@Components/Modules/Helpers";
import type { PropsWithStyleX, QueryXsMax } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const flyerAdSizes: IAdTagViewPortSizes = [
    {
        viewport: [0, 0],
        slot: [
            [640, 960],
            [640, 900],
            [470, 705],
            [470, 661],
            [320, 450],
            [320, 480],
        ],
    },
];

const styles = stylex.create({
    adContainer: {
        [xsMax]: {
            maxWidth: "90vw",
        },
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        height: "100%",
    },
});

type IFlyerCataloguePageAd = IFlyerCatalogueImage &
    PropsWithStyleX & {
        flyerId: string;
        onSlotRenderFail: () => void;
    };

export const FlyerCataloguePageAd: FC<IFlyerCataloguePageAd> = (props) => {
    const slotSizesRef = useRef(flyerAdSizes);
    const [adKey, setAdKey] = useState(0);
    const adRef = useRef<HTMLElement>();

    const { isMobile } = useViewportInfoContext();

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            if (entries.length > 0) {
                const { width, height } = entries[0].contentRect;

                if (!width || !height) {
                    return;
                }

                if (isMobile ? width <= 469 : height <= 660) {
                    slotSizesRef.current[0].slot = [
                        [320, 450],
                        [320, 480],
                    ];
                    setAdKey(1);
                } else if (isMobile ? width <= 639 : height <= 899) {
                    slotSizesRef.current[0].slot = [
                        [320, 480],
                        [470, 661],
                        [470, 705],
                    ];
                    setAdKey(2);
                } else {
                    slotSizesRef.current[0].slot = [
                        [640, 900],
                        [640, 960],
                    ];
                    setAdKey(3);
                }
            }
        });

        observer.observe(adRef.current.closest("li"));
        sendTelemetryEvent({
            eventName: `${TelemetryEntity.Flyer}-${FlyerEventType.pageBanner}`,
            actionType: "View",
            itemId: props.flyerId,
            param1: props.bannerId,
        });
        return () => observer.disconnect();
    }, []);

    const slotRender = (e: googletag.events.SlotRenderEndedEvent) => {
        if (props.onSlotRenderFail && e.isEmpty) {
            props.onSlotRenderFail();
        }
    };

    return (
        <AdTag
            key={adKey}
            ref={adRef}
            optId={`ad-flyer-${props.flyerId}-${props.adContent.adUnitId}`}
            adUnitId={props.adContent.adUnitId}
            slugTarget={props.adContent.slugTarget}
            viewportSizes={slotSizesRef.current}
            styleX={styles.adContainer}
            closeOnLoadFail={false}
            sendUserInfo
            onSlotRender={slotRender}
        />
    );
};
